import React, { useState, useEffect } from "react";
import { FaArrowLeft, FaEdit } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import DashPavimentos from "./DashPavimentos";
import DashEap from "./DashEap";
import DashRankingInsumos from "./DashRankingInsumos";
import EditarEntity from "./EditarEntity";
import "./EntityDetails.css";

const formatarDataCurta = (data) => {
  if (!data) return "—";
  const str = typeof data === "string" ? data.split("T")[0] : null;
  if (str && /^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [ano, mes, dia] = str.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  const d = new Date(data);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatarMoeda = (valor) => {
  if (valor == null || valor === "") return "—";
  const num = Number(valor);
  return isNaN(num) ? "—" : num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const formatarCnpj = (v) => {
  if (!v) return "—";
  const d = String(v).replace(/\D/g, "");
  if (d.length !== 14) return v;
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const extrairTexto = (item) => {
  if (!item) return "";
  return typeof item === "string" ? item : (item.name ?? item.nome ?? "");
};

const parseMesAno = (mesAno) => {
  if (!mesAno) return null;
  const MESES = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
  const [mes, ano] = mesAno.split("/");
  const idx = MESES.findIndex((m) => m.toLowerCase() === mes.toLowerCase());
  return idx === -1 || !ano ? null : new Date(parseInt(ano), idx, 1);
};

const encontrarIndiceParaData = (historico, data) => {
  if (!historico?.length || !data) return null;
  const target = new Date(data);
  let melhor = null, menorDiff = Infinity;
  for (const row of historico) {
    const d = row.data ? new Date(row.data) : parseMesAno(row.mes);
    if (!d || isNaN(d.getTime())) continue;
    const diff = Math.abs(d - target);
    if (diff < menorDiff) { menorDiff = diff; melhor = row; }
  }
  return melhor;
};

const formatarDataHora = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const agora = new Date();
  const diff = Math.floor((agora - d) / 60000);
  const hora = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const data = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  if (diff < 1) return "Agora";
  if (diff < 60) return `Há ${diff}min · ${hora}`;
  if (diff < 1440) return `Às ${hora} de hoje`;
  if (diff < 2880) return `Às ${hora} de ontem`;
  return `Às ${hora} do dia ${data}`;
};

// ── HELPERS ──
const autorDe = (r) => r?.user_id ?? r?.created_by ?? r?.autor_id ?? r?.criado_por ?? r?.responsavel_id ?? null;
const nomePilha = (p) => p?.nome ?? p?.name ?? p?.titulo ?? "";
const pilhaCompartilhada = (p) => !!(p?.compartilhada ?? p?.compartilhado ?? p?.shared ?? p?.is_shared);

const notaInfo = (n) => {
  if (n.tipo === "Atas") return { nome: "ata", tipo: "Atas" };
  if (n.tipo === "Tarefas") return { nome: "tarefa", tipo: "Tarefas" };
  if (n.tipo === "Lista") return { nome: "lista", tipo: "Lista" };
  if (n.tipo === "Diário de Obra") return { nome: "diário de obra", tipo: "Diário de Obra" };
  return { nome: "nota rápida", tipo: "Nota" };
};

const rotuloDia = (ts) => {
  const d = new Date(ts);
  const h = new Date(); h.setHours(0, 0, 0, 0);
  const dia = new Date(d); dia.setHours(0, 0, 0, 0);
  const diff = Math.round((h - dia) / 86400000);
  if (diff <= 0) return "Hoje";
  if (diff === 1) return "Ontem";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
};

const horaCurta = (ts) => new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const FILTROS_ATV = [
  ["todos", "Todos"],
  ["RDO", "RDO"],
  ["Atas", "Atas"],
  ["Nota", "Notas"],
  ["Tarefas", "Tarefas"],
  ["Lista", "Listas"],
  ["Pilha", "Pilhas"],
];

const BOLHAS_RESUMO = [
  { tipo: "Tarefas", label: "Tarefas" },
  { tipo: "Atas", label: "Atas" },
  { tipo: "Lista", label: "Listagens" },
  { tipo: "Diário de Obra", label: "Diários de Obra" },
  { tipo: "Nota", label: "Notas rápidas" },
];

// ── 1. fetchMembros genérico ──
const TABELA_MEMBROS = {
  project: ["project_members", "project_id"],
  setor: ["setor_members", "setor_id"],
  workspace: ["workspace_members", "workspace_id"],
};

async function fetchMembros(tipo, id) {
  if (!id) return [];
  const [tabela, campo] = TABELA_MEMBROS[tipo] || TABELA_MEMBROS.setor;
  const { data: rows, error } = await supabase.from(tabela).select("user_id").eq(campo, id);
  if (error || !rows?.length) return [];
  const ids = rows.map((r) => r.user_id).filter(Boolean);
  if (!ids.length) return [];
  const { data: perfis } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", ids);
  return perfis || [];
}

function hashColor(str) {
  if (!str) return { bg: "#4a6fa5", fg: "#ffffff" };
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  const hue = Math.abs(h) % 360;
  return { bg: `hsl(${hue},55%,42%)`, fg: "#ffffff" };
}

function ProjectAvatar({ name, photoUrl, size = 52 }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").trim().split(/\s+/).filter(Boolean).slice(0,2).map((w) => w[0].toUpperCase()).join("");
  const { bg, fg } = hashColor(name);
  const fs = Math.round(size * 0.34);
  const style = { width: size, height: size, minWidth: size, minHeight: size, borderRadius: "10%", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" };
  if (photoUrl && !err) return <div style={style}><img src={photoUrl} alt={name} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={() => setErr(true)} /></div>;
  return <div style={{ ...style, backgroundColor: bg, color: fg, fontSize: fs, fontWeight: 800, fontFamily: "'Segoe UI',Arial,sans-serif", userSelect:"none" }}>{initials}</div>;
}

function MiniAvatar({ name, avatarUrl, size = 32 }) {
  const [err, setErr] = useState(false);
  const initials = (name || "?").trim().split(/\s+/)[0]?.[0]?.toUpperCase() ?? "?";
  const { bg } = hashColor(name);
  const style = { width: size, height: size, minWidth: size, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: bg, color: "#fff", fontSize: size * 0.38, fontWeight: 700, fontFamily: "'Segoe UI',Arial,sans-serif", flexShrink: 0 };
  if (avatarUrl && !err) return <div style={style}><img src={avatarUrl} alt={name} style={{ width:"100%", height:"100%", objectFit:"cover" }} onError={() => setErr(true)} /></div>;
  return <div style={style}>{initials}</div>;
}

function DonutChart({ percent, size = 72 }) {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (percent / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e8ecf5" strokeWidth={7} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#020a52" strokeWidth={7} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.6s ease" }} />
    </svg>
  );
}

function TimelineBar({ inicio, termino }) {
  const hoje = new Date();
  const start = inicio ? new Date(inicio) : null;
  const end = termino ? new Date(termino) : null;
  let pct = 0;
  if (start && end && end > start) pct = Math.min(100, Math.max(0, ((hoje - start) / (end - start)) * 100));
  return (
    <div className="db-timeline">
      <div className="db-timeline-labels">
        <div className="db-tl-item"><span className="db-tl-sub">INÍCIO</span><span className="db-tl-val">{formatarDataCurta(inicio)}</span></div>
        <div className="db-tl-item db-tl-center"><span className="db-tl-sub">HOJE</span><span className="db-tl-val">{formatarDataCurta(hoje.toISOString())}</span></div>
        <div className="db-tl-item db-tl-right"><span className="db-tl-sub">TÉRMINO</span><span className="db-tl-val">{formatarDataCurta(termino)}</span></div>
      </div>
      <div className="db-timeline-track">
        <div className="db-timeline-fill" style={{ width: `${pct}%` }} />
        <div className="db-timeline-thumb" style={{ left: `${pct}%` }} />
      </div>
      <div className="db-timeline-pct">{Math.round(pct)}% do prazo decorrido</div>
    </div>
  );
}

function tipoIcon(tipo) {
  if (tipo === "Tarefas") return { icon: "task_alt", bg: "#dcfce7", color: "#16a34a" };
  if (tipo === "Atas") return { icon: "description", bg: "#dbeafe", color: "#2563eb" };
  if (tipo === "Lista") return { icon: "list", bg: "#fef9c3", color: "#ca8a04" };
  if (tipo === "Diário de Obra") return { icon: "construction", bg: "#f3e8ff", color: "#9333ea" };
  if (tipo === "Meta") return { icon: "flag", bg: "#fff7ed", color: "#ea580c" };
  if (tipo === "RDO") return { icon: "construction", bg: "#ffedd5", color: "#c2410c" };
  if (tipo === "Pilha") return { icon: "view_column", bg: "#e0e7ff", color: "#4338ca" };
  if (tipo === "Nota") return { icon: "sticky_note_2", bg: "#fce7f3", color: "#be185d" };
  return { icon: "sticky_note_2", bg: "#f3f4f6", color: "#6b7280" };
}

export default function EntityDetails({ entityType, entity: entityProp, onBack, onEdit, children, canEdit = false, containerId }) {
  const isProject = entityType === "project";
  const [entity, setEntity] = useState(entityProp);
  const [showModal, setShowModal] = useState(false);
  const [engenheiro, setEngenheiro] = useState(null);
  const [membros, setMembros] = useState([]);
  const [stats, setStats] = useState({ tarefasTotal: 0, tarefasResolvidas: 0, atas: 0, listagens: 0, diarios: 0 });
  const [loadingStats, setLoadingStats] = useState(true);
  const [inccHistorico, setInccHistorico] = useState([]);
  const [eapComValores, setEapComValores] = useState([]);
  const [eapStats, setEapStats] = useState({});
  const [pavStats, setPavStats] = useState({});
  const [atividades, setAtividades] = useState([]);
  const [membroStats, setMembroStats] = useState({});
  const [orcamentoBaseValor, setOrcamentoBaseValor] = useState(null);
  const [atrasadas, setAtrasadas] = useState([]);
  const [pavimentosComAtividade, setPavimentosComAtividade] = useState(new Map());
  const [filtroAtv, setFiltroAtv] = useState("todos");
  const [notasRapidas, setNotasRapidas] = useState(0);

  const fetchRdosByPavimento = async (pavNome) => {
    try {
      const { data: rdos } = await supabase
        .from("rdos")
        .select("data_rdo, dia_semana, atividades")
        .eq("project_id", entityProp.id)
        .order("data_rdo", { ascending: false });
      
      const filtrados = (rdos || [])
        .filter((r) => r.atividades?.[pavNome]?.trim())
        .map((r) => ({
          data: r.data_rdo 
            ? `${formatarDataCurta(r.data_rdo)}${r.dia_semana ? ` – ${r.dia_semana}` : ""}` 
            : "Data não informada",
          desc: r.atividades[pavNome]
        }));
      
      return filtrados;
    } catch (err) {
      return [];
    }
  };

  // ── 3. useEffect de atividades com suporte a setor ──
  useEffect(() => {
    if (!entityProp?.id) return;
    const acaoLabelMap = {
      criou_nota: "criou",
      editou_nota: "editou",
      criou_lista: "criou lista",
      editou_lista: "editou lista",
      salvou_rascunho: "salvou rascunho de",
      enviou_lista: "enviou",
      concluiu_tarefa: "concluiu",
    };
    const acaoTipoMap = {
      criou_nota: "Nota",
      editou_nota: "Nota",
      criou_lista: "Lista",
      editou_lista: "Lista",
      salvou_rascunho: "Lista",
      enviou_lista: "Lista",
      concluiu_tarefa: "Tarefas",
    };
    const chave = (e) => `${e.uid}|${e.texto}|${Math.floor(new Date(e.ts).getTime() / 60000)}`;
    const foiEditado = (criado, atualizado) =>
      criado && atualizado && new Date(atualizado) - new Date(criado) > 60000;

    (async () => {
      const pid = entityProp.id;
      const campoPilha = isProject ? "project_id" : "setor_id";
      const vazio = Promise.resolve({ data: [] });
      const [{ data: atvProj }, { data: rdos }, { data: pilhas }] = await Promise.all([
        isProject
          ? supabase.from("project_atividades").select("id, tipo_acao, nome_item, user_id, created_at").eq("project_id", pid).order("created_at", { ascending: false }).limit(30)
          : vazio,
        isProject
          ? supabase.from("rdos").select("*").eq("project_id", pid).order("data_rdo", { ascending: false }).limit(20)
          : vazio,
        supabase.from("pilhas").select("*").eq(campoPilha, pid),
      ]);

      const pilhaMap = new Map((pilhas || []).map((p) => [p.id, p]));
      const pilhaIds = (pilhas || []).map((p) => p.id);
      let notas = [];
      if (pilhaIds.length) {
        const { data } = await supabase.from("notas").select("*").in("pilha_id", pilhaIds).order("created_at", { ascending: false }).limit(40);
        notas = data || [];
      }

      const base = (atvProj || []).map((atv) => ({
        _id: `proj_atv_${atv.id}`,
        texto: atv.nome_item || "Item",
        acao: acaoLabelMap[atv.tipo_acao] || atv.tipo_acao.replace(/_/g, " "),
        tipo: acaoTipoMap[atv.tipo_acao] || "Nota",
        uid: atv.user_id,
        ts: atv.created_at,
        pilha: "",
      }));
      const chaves = new Set(base.map(chave));
      const derivados = [];

      (rdos || []).forEach((r) => {
        const criado = r.created_at ?? r.data_rdo;
        if (!criado) return;
        const texto = `RDO de ${formatarDataCurta(r.data_rdo)}`;
        derivados.push({ _id: `rdo_c_${r.id}`, texto, acao: "preencheu", tipo: "RDO", uid: autorDe(r), ts: criado, pilha: "" });
        if (foiEditado(r.created_at, r.updated_at)) {
          derivados.push({ _id: `rdo_u_${r.id}`, texto, acao: "editou", tipo: "RDO", uid: r.updated_by ?? autorDe(r), ts: r.updated_at, pilha: "" });
        }
      });

      notas.forEach((n) => {
        const info = notaInfo(n);
        const texto = n.nome || "Sem título";
        const pilha = nomePilha(pilhaMap.get(n.pilha_id));
        if (n.created_at) derivados.push({ _id: `nota_c_${n.id}`, texto, acao: `criou ${info.nome}`, tipo: info.tipo, uid: autorDe(n), ts: n.created_at, pilha });
        if (foiEditado(n.created_at, n.updated_at)) {
          derivados.push({ _id: `nota_u_${n.id}`, texto, acao: `editou ${info.nome}`, tipo: info.tipo, uid: n.updated_by ?? autorDe(n), ts: n.updated_at, pilha });
        }
      });

      (pilhas || []).forEach((p) => {
        if (!p.created_at || !pilhaCompartilhada(p)) return;
        derivados.push({ _id: `pilha_${p.id}`, texto: nomePilha(p) || "Pilha", acao: "criou pilha compartilhada", tipo: "Pilha", uid: autorDe(p), ts: p.created_at, pilha: "" });
      });

      const todos = [...base, ...derivados.filter((e) => !chaves.has(chave(e)))]
        .filter((e) => e.ts && !isNaN(new Date(e.ts).getTime()))
        .sort((a, b) => new Date(b.ts) - new Date(a.ts))
        .slice(0, 40);

      const uids = [...new Set(todos.map((e) => e.uid).filter(Boolean))];
      let perfis = [];
      if (uids.length) {
        const { data: pf } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", uids);
        perfis = pf || [];
      }

      setAtividades(todos.map((e) => ({ ...e, _perfil: perfis.find((p) => p.id === e.uid) ?? null })));
    })();
  }, [isProject, entityProp?.id]);

  useEffect(() => {
    if (!isProject || !entityProp?.id) return;
    (async () => {
      try {
        const { data: rdos } = await supabase
          .from("rdos")
          .select("atividades, data_rdo")
          .eq("project_id", entityProp.id);

        const mapaAtividade = new Map();
        (rdos || []).forEach((r) => {
          if (r.atividades && typeof r.atividades === "object") {
            Object.entries(r.atividades).forEach(([pavNome, texto]) => {
              if (texto && String(texto).trim() && r.data_rdo) {
                const dataAtual = mapaAtividade.get(pavNome);
                if (!dataAtual || new Date(r.data_rdo) > new Date(dataAtual)) {
                  mapaAtividade.set(pavNome, r.data_rdo);
                }
              }
            });
          }
        });
        setPavimentosComAtividade(mapaAtividade);
      } catch (err) {
        setPavimentosComAtividade(new Map());
      }
    })();
  }, [isProject, entityProp?.id]);

  useEffect(() => {
    if (!isProject || !entityProp?.id) return;
    const fetchData = async () => {
      try {
        const { data: eapData } = await supabase.from("eap").select("id, name, ordem, orcamento_base, bloco, classificacao").eq("project_id", entityProp.id).order("ordem", { ascending: true });
        const { data: pavimentosData } = await supabase.from("pavimentos").select("id, name, ordem, tipo").eq("project_id", entityProp.id).order("ordem", { ascending: true });
        let totalOrcamento = 0;
        if (eapData?.length) for (const e of eapData) totalOrcamento += Number(e.orcamento_base) || 0;
        setOrcamentoBaseValor(totalOrcamento);
        setEapComValores(eapData?.length ? eapData : []);
        setEntity(prev => { const pavChanged = JSON.stringify(prev.pavimentos) !== JSON.stringify(pavimentosData || []); const eapChanged = JSON.stringify(prev.eap) !== JSON.stringify(eapData || []); if (!pavChanged && !eapChanged) return prev; return { ...prev, pavimentos: pavimentosData || [], eap: eapData || [] }; });
      } catch (err) { setEapComValores([]); setOrcamentoBaseValor(null); }
    };
    fetchData();
  }, [isProject, entityProp?.id]);

  useEffect(() => {
    setEntity(entityProp);
    setShowModal(false);
    setEngenheiro(null);
    setEapComValores([]);
    setMembros([]);
    setLoadingStats(true);
    setAtividades([]);
    setAtrasadas([]);
    setStats({ tarefasTotal: 0, tarefasResolvidas: 0, atas: 0, listagens: 0, diarios: 0 });
    setOrcamentoBaseValor(null);
    setNotasRapidas(0);
  }, [entityProp?.id]);

  // ── 2. useEffect de membros genérico ──
  useEffect(() => { if (!entity?.id) return; fetchMembros(entityType, entity.id).then(setMembros); }, [entityType, entity?.id]);

  const name = entity.name || (isProject ? "Projeto" : "Setor");
  const dataInicio = isProject ? entity.data_inicio : null;
  const dataFinalizacao = isProject ? entity.data_finalizacao : null;
  const eap = isProject ? entity.eap || [] : [];
  const pavimentos = isProject ? entity.pavimentos || [] : [];
  const orcamentoBaseFinal = orcamentoBaseValor !== null && orcamentoBaseValor > 0 ? orcamentoBaseValor : entity.orcamento_base;

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from("incc_historico")
          .select("mes, indice, data_referencia, variacao_no_mes, variacao_no_ano, variacao_12_meses")
          .order("data_referencia", { ascending: true });
        if (error) throw error;
        const historico = (data || []).map((r) => ({
          mes: r.mes,
          data: r.data_referencia,
          indice: parseFloat(String(r.indice).replace(",", ".")),
          variacao: {
            no_mes: r.variacao_no_mes != null ? parseFloat(String(r.variacao_no_mes).replace(",", ".")) : null,
            no_ano: r.variacao_no_ano != null ? parseFloat(String(r.variacao_no_ano).replace(",", ".")) : null,
            doze_meses: r.variacao_12_meses != null ? parseFloat(String(r.variacao_12_meses).replace(",", ".")) : null,
          },
        }));
        setInccHistorico(historico);
      } catch (err) {
        console.warn("Erro ao carregar histórico INCC do Supabase:", err);
        setInccHistorico([]);
      }
    })();
  }, []);

  // ── 2. useEffect de responsável genérico ──
  useEffect(() => {
    const id = isProject ? entity.engenheiro_id : entity.responsavel_id;
    if (!id) { setEngenheiro(null); return; }
    supabase.from("profiles").select("id, nickname, avatar_url").eq("id", id).single()
      .then(({ data, error }) => { if (!error) setEngenheiro(data); })
      .catch(() => {});
  }, [isProject, entity.engenheiro_id, entity.responsavel_id]);

  useEffect(() => {
    if (!entityProp.id) {
      setStats({ tarefasTotal: 0, tarefasResolvidas: 0, atas: 0, listagens: 0, diarios: 0 });
      setLoadingStats(false);
      return;
    }
    setLoadingStats(true);
    (async () => {
      try {
        const pilhasQuery = isProject
        ? supabase.from("pilhas").select("id, responsavel_id").eq("project_id", entityProp.id)
        : supabase.from("pilhas").select("id, responsavel_id").eq("setor_id", entityProp.id);
        const { data: pilhas, error: errPilhas } = await pilhasQuery;
        if (errPilhas) console.error("Erro ao buscar pilhas:", errPilhas);

        if (!pilhas?.length) {
          setStats({ tarefasTotal: 0, tarefasResolvidas: 0, atas: 0, listagens: 0, diarios: 0 });
          setLoadingStats(false);
          return;
        }

        const pilhaIds = pilhas.map((p) => p.id);
        const { data: notas, error: errNotas } = await supabase
          .from("notas")
          .select("id, tipo, concluida, pilha_id, created_at, nome, data_entrega, responsavel")
          .in("pilha_id", pilhaIds)
          .order("created_at", { ascending: false });
        if (errNotas) console.error("Erro ao buscar notas:", errNotas);
        if (!notas) { setStats({ tarefasTotal: 0, tarefasResolvidas: 0, atas: 0, listagens: 0, diarios: 0 }); setLoadingStats(false); return; }
        const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
        const tarefas = notas.filter((n) => n.tipo === "Tarefas");
        setStats({ tarefasTotal: tarefas.length, tarefasResolvidas: tarefas.filter((n) => n.concluida).length, atas: notas.filter((n) => n.tipo === "Atas").length, listagens: notas.filter((n) => n.tipo === "Lista").length, diarios: notas.filter((n) => n.tipo === "Diário de Obra").length });
        setNotasRapidas(notas.filter((n) => n.tipo === "Nota Rápida").length);
        if (isProject) {
          const eapMap = {}, pavMap = {};
          for (const pilha of pilhas) {
            const pilhaTarefas = notas.filter((n) => n.pilha_id === pilha.id && n.tipo === "Tarefas");
            if (pilha.eap_id) { if (!eapMap[pilha.eap_id]) eapMap[pilha.eap_id] = { total: 0, resolvidas: 0 }; eapMap[pilha.eap_id].total += pilhaTarefas.length; eapMap[pilha.eap_id].resolvidas += pilhaTarefas.filter((n) => n.concluida).length; }
            if (pilha.pavimento_id) { if (!pavMap[pilha.pavimento_id]) pavMap[pilha.pavimento_id] = { total: 0, resolvidas: 0 }; pavMap[pilha.pavimento_id].total += pilhaTarefas.length; pavMap[pilha.pavimento_id].resolvidas += pilhaTarefas.filter((n) => n.concluida).length; }
          }
          setEapStats(eapMap); setPavStats(pavMap);
        }
        const mbrMap = {};
        for (const n of tarefas) { const uid = n.responsavel || pilhas.find((p) => p.id === n.pilha_id)?.responsavel_id; if (!uid) continue; if (!mbrMap[uid]) mbrMap[uid] = { total: 0, atrasadas: 0 }; mbrMap[uid].total++; const atrasada = !n.concluida && n.data_entrega && new Date(n.data_entrega) < hoje; if (atrasada) mbrMap[uid].atrasadas++; }
        setMembroStats(mbrMap);
        const { data: perfisAtv } = await supabase.from("profiles").select("id, nickname, avatar_url");
        const vencidas = tarefas.filter((n) => !n.concluida && n.data_entrega && new Date(n.data_entrega) < hoje);
        const uidsVenc = [...new Set(vencidas.map((n) => n.responsavel).filter(Boolean))];
        let perfisVenc = perfisAtv || [];
        if (uidsVenc.some((u) => !perfisVenc.find((p) => p.id === u))) { const { data: pf2 } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", uidsVenc); perfisVenc = [...perfisVenc, ...(pf2 || [])]; }
        setAtrasadas(vencidas.map((n) => ({ ...n, _perfil: perfisVenc.find((p) => p.id === n.responsavel) ?? null })));
      } catch (err) { console.error("Erro ao carregar stats:", err); }
      finally { setLoadingStats(false); }
    })();
  }, [isProject, entityProp?.id]);
  
  const progresso = stats.tarefasTotal > 0 ? Math.round((stats.tarefasResolvidas / stats.tarefasTotal) * 100) : 0;
  const inccInicio = encontrarIndiceParaData(inccHistorico, dataInicio);
  const inccAtual = inccHistorico?.length ? inccHistorico[inccHistorico.length - 1] : null;
  const fatorIncc = inccInicio && inccAtual && inccInicio.indice > 0 ? inccAtual.indice / inccInicio.indice : null;
  const varIncc = fatorIncc ? ((fatorIncc - 1) * 100).toFixed(2) : null;
  const orcamentoCorrigidoCalculado = orcamentoBaseFinal && fatorIncc ? orcamentoBaseFinal * fatorIncc : null;

  const valoresResumo = {
    "Tarefas": `${stats.tarefasResolvidas}/${stats.tarefasTotal}`,
    "Atas": stats.atas,
    "Lista": stats.listagens,
    "Diário de Obra": stats.diarios,
    "Nota": notasRapidas,
  };

  // ── 5. Bolhas reutilizáveis ──
  const bolhasJsx = (
    <div className="db-bolhas">
      {BOLHAS_RESUMO.map(({ tipo, label }) => {
        const ic = tipoIcon(tipo);
        return (
          <span key={tipo} className="db-bolha" title={label} style={{ "--bolha-bg": ic.bg, "--bolha-fg": ic.color }}>
            <span className="db-bolha-ico"><span className="material-symbols-outlined">{ic.icon}</span></span>
            <span className="db-bolha-num">{loadingStats ? "…" : valoresResumo[tipo]}</span>
          </span>
        );
      })}
    </div>
  );

  const atividadesFiltradas = filtroAtv === "todos" ? atividades : atividades.filter((a) => a.tipo === filtroAtv);
  const atividadesAgrupadas = atividadesFiltradas.reduce((acc, a) => {
    const rotulo = rotuloDia(a.ts);
    const ultimo = acc[acc.length - 1];
    if (ultimo && ultimo.rotulo === rotulo) ultimo.itens.push(a);
    else acc.push({ rotulo, itens: [a] });
    return acc;
  }, []);

  const enderecoCompleto = [
    [entity.endereco, entity.numero].filter(Boolean).join(", "),
    entity.complemento,
    entity.bairro,
    [entity.cidade, entity.uf].filter(Boolean).join(" - "),
  ].filter(Boolean).join(" · ");

  // ── 4. radarAlertas genérico (projeto e setor) ──
  const radarAlertas = [];
  if (atrasadas.length > 0) {
    atrasadas.slice(0, 4).forEach((n) => {
      const diasAtraso = Math.floor((Date.now() - new Date(n.data_entrega).getTime()) / 86400000);
      const responsavel = n._perfil?.nickname ? ` · ${n._perfil.nickname}` : "";
      radarAlertas.push({ tipo: "warn", texto: n.nome || "Tarefa sem título", sub: `Atrasada há ${diasAtraso} dia${diasAtraso !== 1 ? "s" : ""}${responsavel} · prazo: ${formatarDataCurta(n.data_entrega)}` });
    });
    if (atrasadas.length > 4) radarAlertas.push({ tipo: "warn", texto: `+${atrasadas.length - 4} tarefa${atrasadas.length - 4 !== 1 ? "s" : ""} atrasada${atrasadas.length - 4 !== 1 ? "s" : ""}`, sub: "Verifique o Kanban para ver todas." });
  }
  if (progresso >= 70) radarAlertas.push({ tipo: "ok", texto: "Produtividade acima da média", sub: "Mantenha o ritmo atual!" });
  if (entity.double_check_enabled) radarAlertas.push({ tipo: "info", texto: "Double Check ativo", sub: "Objetivos precisam de 2 aprovações." });
  if (!radarAlertas.length) radarAlertas.push({ tipo: "ok", texto: "Nenhum item atrasado", sub: "Todas as tarefas estão em dia. 🎉" });

  // ── 2. handleSaved com fetchMembros ──
  const handleSaved = (updated) => {
    setEntity(updated);
    if (updated._membrosAtualizados) setMembros(updated._membrosAtualizados);
    else fetchMembros(entityType, updated.id).then(setMembros);
    if (onEdit) onEdit(updated);
  };

  return (
    <>
      <div className="db-wrap">
        {/* ── 6. Header condicional (projeto vs setor) ── */}
        <div className="db-header">
          <div className="db-header-left">
            <button type="button" className="db-back" onClick={onBack}><FaArrowLeft /></button>
            <ProjectAvatar name={name} photoUrl={entity.photo_url} size={50} />
            <div className="db-header-info">
              <div className="db-header-top-row">
                <h1 className="db-title">{name}</h1>
                {isProject ? (
                  <>
                    <span className="db-status-pill db-status-andamento">Em andamento</span>
                    {entity.tipo && <span className="db-tag">{entity.tipo}</span>}
                    {entity.padrao && <span className="db-tag">{entity.padrao}</span>}
                  </>
                ) : (
                  <span className="db-tag">Setor</span>
                )}
              </div>
              {isProject ? (
                <div className="db-header-dates">
                  <span className="db-hdate"><span className="db-hdate-lbl">INÍCIO</span>{formatarDataCurta(dataInicio)}</span>
                  <span className="db-hdate"><span className="db-hdate-lbl">TÉRMINO</span>{formatarDataCurta(dataFinalizacao)}</span>
                </div>
              ) : (
                <div className="db-resp">
                 
                </div>
              )}
            </div>
          </div>

          {isProject && (
            <div className="db-header-center">
              <div className="db-obra-info">
                <span className="db-obra-info-title">INFORMAÇÕES DA OBRA</span>
                <div className="db-obra-info-row">
                  <span className="db-obra-info-lbl">Razão social</span>
                  <span className="db-obra-info-val" title={entity.razao_social || ""}>{entity.razao_social || "—"}</span>
                </div>
                <div className="db-obra-info-row">
                  <span className="db-obra-info-lbl">CNPJ</span>
                  <span className="db-obra-info-val">{formatarCnpj(entity.cnpj)}</span>
                </div>
                <div className="db-obra-info-row">
                  <span className="db-obra-info-lbl">Endereço</span>
                  <span className="db-obra-info-val" title={enderecoCompleto}>{enderecoCompleto || "—"}</span>
                </div>
              </div>
            </div>
          )}

          {isProject && <div className="db-header-right"><TimelineBar inicio={dataInicio} termino={dataFinalizacao} /></div>}
          <div className="db-header-actions">
            {canEdit && <button type="button" className="db-btn-edit" onClick={() => setShowModal(true)}><FaEdit /> Editar</button>}
          </div>
        </div>

        {isProject && (
          <div className="db-orcamento-row">
            <div className="db-orc-card">
              <span className="db-orc-lbl">Orçamento base</span>
              <span className="db-orc-val">{orcamentoBaseFinal ? formatarMoeda(orcamentoBaseFinal) : "—"}</span>
              <span className="db-orc-sub">Soma dos valores da EAP</span>
            </div>
            <div className="db-orc-card">
              <span className="db-orc-lbl">Variação INCC</span>
              <span className={`db-orc-val ${varIncc && Number(varIncc) >= 0 ? "db-pos" : "db-neg"}`}>{varIncc ? `${Number(varIncc) >= 0 ? "▲ +" : "▼ "}${varIncc}%` : "—"}</span>
              <span className="db-orc-sub">Acumulado desde o início</span>
            </div>
            <div className="db-orc-card db-orc-card--dest">
              <span className="db-orc-lbl">Orçamento corrigido</span>
              <span className="db-orc-val db-orc-dest">{orcamentoCorrigidoCalculado ? formatarMoeda(orcamentoCorrigidoCalculado) : (orcamentoBaseFinal ? formatarMoeda(orcamentoBaseFinal) : "—")}</span>
              <span className="db-orc-sub">Com base no INCC atual</span>
            </div>
            <div className="db-orc-card">
              <span className="db-orc-lbl">INCC</span>
              <div className="db-incc-ref-row">
                <span className="db-incc-idx">{inccInicio ? Number(inccInicio.indice).toLocaleString("pt-BR", { minimumFractionDigits: 3 }) : "—"}</span>
                <span className="db-incc-arrow">→</span>
                <span className="db-incc-idx">{inccAtual ? Number(inccAtual.indice).toLocaleString("pt-BR", { minimumFractionDigits: 3 }) : "—"}</span>
              </div>
              <span className="db-orc-sub">Referência → atual</span>
            </div>
            <div className="db-orc-card db-orc-card--notas">
              <span className="db-orc-lbl">Notas</span>
              {bolhasJsx}
              <span className="db-orc-sub">Tarefas · Atas · Listagens · Diários · Rápidas</span>
            </div>
          </div>
        )}

        {/* ── 7. Layout unificado ── */}
        {!isProject && (
          <div className="db-resumo-setor">
            <span className="db-orc-lbl">Notas</span>
            {bolhasJsx}
          </div>
        )}

        {isProject && (
          <div className="db-main-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div className="db-panel">
              <DashEap eapList={eapComValores} orcamentoTotal={orcamentoBaseFinal} fatorIncc={fatorIncc} />
            </div>
            <div className="db-panel">
              <DashPavimentos
                pavimentos={pavimentos}
                locacoes={pavimentos.filter(p => p?.tipo === "locacao")}
                pavStats={pavStats}
                pavimentosComAtividade={pavimentosComAtividade}
                onVerRegistros={(pav, openModal) => {
                  const pavNome = extrairTexto(pav);
                  fetchRdosByPavimento(pavNome).then((rdos) => {
                    openModal(pavNome, async () => rdos);
                  });
                }}
              />
            </div>
            <div className="db-panel">
              <DashRankingInsumos projectId={entityProp.id} />
            </div>
          </div>
        )}

        <div className={`db-bottom-grid${isProject ? "" : " db-bottom-grid--setor"}`}>
          <div className="db-panel1">
            <div className="db-panel-header">
              <span className="db-panel-title">EQUIPE</span>
            </div>
            <div className="db-equipe-list">
              {engenheiro && (
                <div className="db-membro-row db-membro-row--eng">
                  <MiniAvatar name={engenheiro.nickname} avatarUrl={engenheiro.avatar_url} size={32} />
                  <div className="db-membro-info">
                    <span className="db-membro-name">{engenheiro.nickname}</span>
                    <span className="db-membro-cargo">{isProject ? "Engenheiro responsável" : "Responsável"}</span>
                  </div>
                  <div className="db-membro-stats">
                    <span className="db-membro-stat-num">{membroStats[engenheiro.id]?.total ?? "—"}</span>
                    <span className="db-membro-stat-lbl">tarefas</span>
                  </div>
                  <div className="db-membro-stats">
                    <span className="db-membro-stat-num db-atr">{membroStats[engenheiro.id]?.atrasadas ?? "—"}</span>
                    <span className="db-membro-stat-lbl">atrasadas</span>
                  </div>
                  <span className="db-membro-dot db-dot-green" />
                </div>
              )}
              {membros.map((m) => !m?.id ? null : (
                <div key={m.id} className="db-membro-row">
                  <MiniAvatar name={m.nickname} avatarUrl={m.avatar_url} size={32} />
                  <div className="db-membro-info">
                    <span className="db-membro-name">{m.nickname}</span>
                    <span className="db-membro-cargo">Membro</span>
                  </div>
                  <div className="db-membro-stats">
                    <span className="db-membro-stat-num">{membroStats[m.id]?.total ?? "—"}</span>
                    <span className="db-membro-stat-lbl">tarefas</span>
                  </div>
                  <div className="db-membro-stats">
                    <span className="db-membro-stat-num db-atr">{membroStats[m.id]?.atrasadas ?? "—"}</span>
                    <span className="db-membro-stat-lbl">atrasadas</span>
                  </div>
                  <span className="db-membro-dot db-dot-green" />
                </div>
              ))}
              {!engenheiro && membros.length === 0 && <p className="db-empty">Nenhum membro cadastrado</p>}
            </div>
          </div>

          <div className="db-panel1">
            <div className="db-panel-header">
              <span className="db-panel-title">ATIVIDADES RECENTES</span>
              <span className="db-panel-updated">{atividadesFiltradas.length} registro{atividadesFiltradas.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="atv-filtros">
              {FILTROS_ATV.filter(([key]) => isProject || key !== "RDO").map(([key, label]) => (
                <button key={key} type="button" className={`atv-chip ${filtroAtv === key ? "atv-chip--on" : ""}`} onClick={() => setFiltroAtv(key)}>{label}</button>
              ))}
            </div>
            <div className="atv-feed">
              {atividadesAgrupadas.map((g) => (
                <div key={g.rotulo} className="atv-grupo">
                  <div className="atv-grupo-label">{g.rotulo}</div>
                  {g.itens.map((a, i) => {
                    const ic = tipoIcon(a.tipo);
                    const quem = a._perfil?.nickname || a.nomeFallback || "Alguém";
                    return (
                      <div key={a._id ?? i} className="atv-item">
                        <div className="atv-avatar">
                          <MiniAvatar name={quem} avatarUrl={a._perfil?.avatar_url} size={30} />
                          <span className="atv-badge" style={{ background: ic.bg, color: ic.color }}><span className="material-symbols-outlined">{ic.icon}</span></span>
                        </div>
                        <div className="atv-body">
                          <span className="atv-texto"><strong>{quem}</strong> {a.acao} <em>"{a.texto}"</em></span>
                          {a.pilha && <span className="atv-pilha">🗂 {a.pilha}</span>}
                        </div>
                        <span className="atv-hora">{horaCurta(a.ts)}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
              {atividadesFiltradas.length === 0 && !loadingStats && <p className="db-empty">Nenhuma atividade recente</p>}
              {loadingStats && atividadesFiltradas.length === 0 && <p className="db-empty">Carregando...</p>}
            </div>
          </div>

          <div className="db-panel1">
            <div className="db-panel-header">
              <span className="db-panel-title">RADAR DA OBRA</span>
              {atrasadas.length > 0 ? <span className="db-panel-badge-warn">{atrasadas.length} atrasada{atrasadas.length !== 1 ? "s" : ""}</span> : <span className="db-panel-updated">Atualizado hoje</span>}
            </div>
            <div className="db-radar-list">
              {radarAlertas.map((al, i) => (
                <div key={i} className={`db-radar-item db-radar-${al.tipo}`}>
                  <span className="db-radar-icon">{al.tipo === "warn" ? "⚠" : al.tipo === "ok" ? "✓" : "ℹ"}</span>
                  <div className="db-radar-body">
                    <span className="db-radar-texto">{al.texto}</span>
                    <span className="db-radar-sub">{al.sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {children && <div className="db-children">{children}</div>}
      </div>

      {showModal && (
        <EditarEntity 
          entityType={entityType}
          entity={entity} 
          eap={eap} 
          onClose={() => setShowModal(false)} 
          onSave={handleSaved} 
          inccHistorico={inccHistorico} 
          containerId={containerId} 
        />
      )}
    </>
  );
}